import React, { useCallback, useEffect, useMemo, useState } from 'react';

// One remembered range across all Data tabs + sessions: pick 3M on Sleep and
// Heart Rate is on 3M too. Persisted so switching tabs never silently resets you.
const STORE_KEY = 'vg_data_range';
const readStored = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch { return {}; }
};

// The one time-range control shared by every Data tab. Before this, each chart
// shipped its own RANGES array (some 1D..All, some starting at 1M, sleep had 6M…)
// — visually and behaviourally inconsistent. Now they all consume the same
// canonical set + a Custom date window, rendered as a single non-wrapping row.
//
// A tab uses the hook for state/filtering and drops <RangeSelector {...selectorProps} />
// wherever its old selector sat:
//   const { inRange, spanDays, selectorProps } = useTimeRange('1M');
//   const filtered = useMemo(() => data.filter(d => inRange(d.date)), [data, inRange]);
//   <RangeSelector {...selectorProps} right={<UnitToggle/>} />

export const DATA_RANGES = [
  { key: '1D', label: '1D', days: 1 },
  { key: '1W', label: '1W', days: 7 },
  { key: '1M', label: '1M', days: 30 },
  { key: '3M', label: '3M', days: 90 },
  { key: '1Y', label: '1Y', days: 365 },
  { key: 'ALL', label: 'All', days: null },
  { key: 'CUSTOM', label: 'Custom', days: null },
];

// YYYY-MM-DD for N days ago, built from LOCAL calendar fields (not toISOString,
// which is UTC and rolls the day near the boundary — the recurring date bug).
const localDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export function useTimeRange(initialKey = '1M') {
  const [rangeKey, setRangeKey] = useState(() => readStored().rangeKey || initialKey);
  const [customStart, setCustomStart] = useState(() => readStored().customStart || '');
  const [customEnd, setCustomEnd] = useState(() => readStored().customEnd || '');

  // Remember the choice across tabs and visits.
  useEffect(() => {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify({ rangeKey, customStart, customEnd }));
    } catch { /* storage unavailable — fall back to in-memory only */ }
  }, [rangeKey, customStart, customEnd]);

  // Stable predicate so consumers' useMemo(deps:[inRange]) only re-run on a real
  // range change, not every render.
  const inRange = useCallback((dateStr) => {
    if (!dateStr) return false;
    if (rangeKey === 'ALL') return true;
    if (rangeKey === 'CUSTOM') {
      if (customStart && dateStr < customStart) return false;
      if (customEnd && dateStr > customEnd) return false;
      return true;
    }
    const days = DATA_RANGES.find((r) => r.key === rangeKey)?.days;
    if (!days) return true;
    return dateStr >= localDaysAgo(days);
  }, [rangeKey, customStart, customEnd]);

  // Effective width of the window in days — drives label granularity / aggregation.
  // Infinity for All (and an incomplete Custom), so `spanDays <= 90` reads false.
  const spanDays = useMemo(() => {
    if (rangeKey === 'ALL') return Infinity;
    if (rangeKey === 'CUSTOM') {
      if (customStart && customEnd) {
        const ms = new Date(customEnd + 'T00:00:00') - new Date(customStart + 'T00:00:00');
        return Math.max(1, Math.round(ms / 86400000) + 1);
      }
      return Infinity;
    }
    return DATA_RANGES.find((r) => r.key === rangeKey)?.days ?? Infinity;
  }, [rangeKey, customStart, customEnd]);

  return {
    inRange,
    spanDays,
    selectorProps: { rangeKey, setRangeKey, customStart, setCustomStart, customEnd, setCustomEnd },
  };
}

const dateInputCls =
  'rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 ' +
  'px-2 py-1 text-sm text-gray-800 dark:text-gray-100';

// `right` is an optional slot for a tab's own extra controls (a unit toggle, an
// entries count) so they sit on the same line as the range buttons.
export function RangeSelector({
  rangeKey, setRangeKey, customStart, setCustomStart, customEnd, setCustomEnd, right = null,
}) {
  return (
    <div className="space-y-2">
      <style>{`.vg-range-scroll::-webkit-scrollbar{display:none}`}</style>
      <div className="flex items-center justify-between gap-2">
        <div
          className="vg-range-scroll flex gap-1 overflow-x-auto bg-gray-100 dark:bg-surface-dark-raised rounded-lg p-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {DATA_RANGES.map((r) => (
            <button
              key={r.key}
              onClick={() => setRangeKey(r.key)}
              className={`shrink-0 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                rangeKey === r.key
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
        {right}
      </div>
      {rangeKey === 'CUSTOM' && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <label className="text-gray-600 dark:text-gray-300">From</label>
          <input type="date" value={customStart} max={customEnd || undefined}
                 onChange={(e) => setCustomStart(e.target.value)} className={dateInputCls} aria-label="Range start" />
          <label className="text-gray-600 dark:text-gray-300">to</label>
          <input type="date" value={customEnd} min={customStart || undefined}
                 onChange={(e) => setCustomEnd(e.target.value)} className={dateInputCls} aria-label="Range end" />
        </div>
      )}
    </div>
  );
}
